/**
 * Answer validation utilities for interactive lessons
 */

/**
 * Normalizes a string for comparison (lowercase, trim, remove extra spaces)
 * @param {string} str - String to normalize
 * @returns {string} - Normalized string
 */
const normalizeString = (str) => {
  if (typeof str !== 'string') {
    return String(str).toLowerCase().trim().replace(/\s+/g, ' ');
  }
  return str.toLowerCase().trim().replace(/\s+/g, ' ');
};

/**
 * Validates an answer against checkingLogic
 * @param {*} userInput - User's answer (can be string, object, array, etc.)
 * @param {Object} checkingLogic - Checking logic from screen
 * @returns {Object} - { isCorrect: boolean, feedback: string, score: number }
 */
export const validateAnswer = (userInput, checkingLogic) => {
  if (!checkingLogic) {
    return {
      isCorrect: false,
      feedback: 'No validation criteria available',
      score: 0
    };
  }

  // Try exact match first
  if (checkingLogic.expectedAnswer !== undefined && checkingLogic.expectedAnswer !== null && checkingLogic.expectedAnswer !== '') {
    const normalizedExpected = normalizeString(checkingLogic.expectedAnswer);
    const normalizedUser = normalizeString(userInput);
    
    if (normalizedExpected === normalizedUser) {
      return {
        isCorrect: true,
        feedback: checkingLogic.feedbackCorrect || 'Correct!',
        score: 100
      };
    }
  }

  // Try acceptable answers array
  if (checkingLogic.acceptableAnswers && Array.isArray(checkingLogic.acceptableAnswers) && checkingLogic.acceptableAnswers.length > 0) {
    const normalizedUser = normalizeString(userInput);
    const matches = checkingLogic.acceptableAnswers.some(acc => {
      const normalizedAcc = normalizeString(acc);
      return normalizedAcc === normalizedUser;
    });

    if (matches) {
      return {
        isCorrect: true,
        feedback: checkingLogic.feedbackCorrect || 'Correct!',
        score: 100
      };
    }
  }

  // If no match found, return incorrect
  return {
    isCorrect: false,
    feedback: checkingLogic.feedbackIncorrect || 'Not quite right. Try again!',
    score: 0
  };
};

/**
 * Validates answer with AI fallback (for complex cases)
 * This should call the backend API for AI validation
 * @param {string} lessonId - Lesson ID
 * @param {string} screenId - Screen ID
 * @param {*} userInput - User's answer
 * @param {Function} apiCall - Function to make API call
 * @returns {Promise<Object>} - { isCorrect: boolean, feedback: string, score: number }
 */
export const validateAnswerWithAI = async (lessonId, screenId, userInput, apiCall) => {
  try {
    const response = await apiCall(lessonId, { screenId, answer: userInput });
    return {
      isCorrect: response.data.isCorrect || false,
      feedback: response.data.feedback || 'Answer evaluated',
      score: response.data.isCorrect ? 100 : 0
    };
  } catch (error) {
    console.error('AI validation error:', error);
    return {
      isCorrect: false,
      feedback: 'Unable to validate answer. Please try again.',
      score: 0
    };
  }
};

/**
 * Validates answer with optimistic UI update
 * First tries local validation, then falls back to AI if needed
 * @param {*} userInput - User's answer
 * @param {Object} checkingLogic - Checking logic from screen
 * @param {Object} options - Options object
 * @param {string} options.lessonId - Lesson ID for AI fallback
 * @param {string} options.screenId - Screen ID for AI fallback
 * @param {Function} options.apiCall - API call function for AI validation
 * @param {boolean} options.useAI - Whether to use AI validation
 * @returns {Promise<Object>} - { isCorrect: boolean, feedback: string, score: number, needsAI: boolean }
 */
export const validateAnswerOptimistic = async (userInput, checkingLogic, options = {}) => {
  // First try local validation
  const localResult = validateAnswer(userInput, checkingLogic);

  // If we have a clear match, return immediately
  if (localResult.isCorrect || (checkingLogic.expectedAnswer && checkingLogic.acceptableAnswers && checkingLogic.acceptableAnswers.length > 0)) {
    return {
      ...localResult,
      needsAI: false
    };
  }

  // For complex cases (coding, open questions), use AI if available
  if (options.useAI && options.lessonId && options.screenId && options.apiCall) {
    try {
      const aiResult = await validateAnswerWithAI(
        options.lessonId,
        options.screenId,
        userInput,
        options.apiCall
      );
      return {
        ...aiResult,
        needsAI: true
      };
    } catch (error) {
      // Fall back to local result if AI fails
      return {
        ...localResult,
        needsAI: false
      };
    }
  }

  return {
    ...localResult,
    needsAI: false
  };
};
